import React, { useState } from 'react';
import { Alert, Badge, Button, Table } from 'react-bootstrap';
import { get } from 'lodash';
import { toast } from 'react-toastify';
import { useGetConfigQuery } from 'services/api';
import { useRetestMutation } from 'services/api/creativePreview';
import { fetchAssetText } from 'containers/NewCreativePreview/components/Carousel';
import BootstrapLogPreviewModal from './BootstrapLogPreviewModal';
import {
  formatTimestamp,
  getCreativePreviewHref,
  getSourceStyle,
  getTestStatusVariant,
} from './utils';

export default function PodTestsTable({
  podName,
  tests,
}: {
  podName: string;
  tests: any[];
}) {
  const [retest] = useRetestMutation();
  const { data: appConfig } = useGetConfigQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [activeLogs, setActiveLogs] = useState('');
  const [logsOpen, setLogsOpen] = useState(false);
  const [loadingLogsFor, setLoadingLogsFor] = useState<string | null>(null);
  const [retestingId, setRetestingId] = useState<string | null>(null);
  const cdnUrl = appConfig?.cdnUrl;

  const handleViewLogs = async test => {
    const s3Prefix = get(test, 's3Prefix');
    const testId = get(test, 'testMetadata.testId') || get(test, '_id');

    if (!s3Prefix || !cdnUrl) {
      toast.info(`Logs are not available yet for ${testId}.`);
      return;
    }

    try {
      setLoadingLogsFor(String(testId));
      const logs = await fetchAssetText(cdnUrl, 'progress.txt', s3Prefix);

      if (!logs) {
        toast.info(`Log file not available for ${testId}.`);
        return;
      }

      setActiveLogs(logs);
      setLogsOpen(true);
    } catch (_error) {
      toast.info(`Log file not available for ${testId}.`);
    } finally {
      setLoadingLogsFor(null);
    }
  };

  const handleRetest = async test => {
    const id = get(test, '_id');
    const testId = get(test, 'testMetadata.testId') || id;
    const testType = get(test, 'testTag');

    if (!id) {
      toast.error('Unable to retest this test case.');
      return;
    }

    if (!testType) {
      toast.error(`Test tag not available for ${testId}.`);
      return;
    }

    try {
      setRetestingId(String(id));
      await retest({ id, testType }).unwrap();
      toast.success(`Retest triggered for ${testId}.`);
    } catch (error) {
      toast.error(get(error, 'data.error', 'Failed to trigger retest'));
    } finally {
      setRetestingId(null);
    }
  };

  if (!tests.length) {
    return (
      <Alert variant="light" className="mb-0">
        No recent Adsure test cases were found for <strong>{podName}</strong>.
      </Alert>
    );
  }

  return (
    <>
      <div className="adsure-table-scroll">
        <Table hover className="align-middle mb-0">
          <thead>
            <tr>
              <th>Test ID</th>
              <th>Source</th>
              <th>Status</th>
              <th>Error Message</th>
              <th className="adsure-country-column">Country</th>
              <th>Adslot</th>
              <th>Timestamp</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tests.map(test => {
              const id = get(test, '_id');
              const testId = get(test, 'testMetadata.testId') || id || 'NA';
              const status = get(test, 'status', 'Unknown');
              const source = get(test, 'source', '');
              const { Icon: SourceIcon, color: sourceColor } =
                getSourceStyle(source);
              const errorMessage = get(test, 'error') || '-';
              const country =
                get(test, 'testMetadata.country_code_3') ||
                get(test, 'country') ||
                '-';
              const adSlot = get(test, 'adSlot') || '-';
              const testHref = getCreativePreviewHref(test);
              const timestamp = formatTimestamp(
                get(test, 'monitoringTimestamp') ||
                  get(test, 'timestamp') ||
                  get(test, 'updatedAt') ||
                  get(test, 'fetchTimestamp') ||
                  get(test, 'createdAt'),
              );

              return (
                <tr key={String(id || testId)}>
                  <td style={{ minWidth: 180 }}>
                    <a
                      href={testHref}
                      className="fw-semibold"
                      style={{ color: '#165177', textDecoration: 'none' }}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {testId}
                    </a>
                  </td>
                  <td style={{ width: 72, minWidth: 72 }}>
                    <div className="d-inline-flex align-items-center justify-content-center w-100">
                      <span
                        className="adsure-source-icon"
                        style={{
                          background: `${sourceColor}18`,
                          color: sourceColor,
                        }}
                      >
                        <SourceIcon />
                      </span>
                    </div>
                  </td>
                  <td style={{ minWidth: 150 }}>
                    <Badge bg={getTestStatusVariant(status)}>{status}</Badge>
                  </td>
                  <td style={{ minWidth: 240 }}>
                    <div
                      style={{
                        maxWidth: 280,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={errorMessage}
                    >
                      {errorMessage}
                    </div>
                  </td>
                  <td className="adsure-country-column">{country}</td>
                  <td style={{ minWidth: 220 }}>{adSlot}</td>
                  <td style={{ minWidth: 180 }}>{timestamp}</td>
                  <td className="text-end" style={{ minWidth: 190 }}>
                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => handleRetest(test)}
                        disabled={retestingId === String(id)}
                      >
                        {retestingId === String(id) ? 'Retesting...' : 'Retest'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => handleViewLogs(test)}
                        disabled={loadingLogsFor === String(testId)}
                      >
                        {loadingLogsFor === String(testId)
                          ? 'Loading...'
                          : 'View Logs'}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
      {logsOpen ? (
        <BootstrapLogPreviewModal
          open={logsOpen}
          setOpen={setLogsOpen}
          logs={activeLogs}
        />
      ) : null}
    </>
  );
}
